/**
 * TrainingContext — Global Training Provider
 *
 * Mounted near the top of the app provider tree.
 * Provides shared plumbing to all <TrainingScope> wrappers:
 * - Auth token for API calls
 * - Eligibility fetching (cached)
 * - Module completion recording
 *
 * @see IAT-001: Embedded Interactive Training System
 */

import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useAuthtoken } from 'stores/useAuthStore';
import { useUser } from 'stores/useSessionStore';
import { isGlobalAdminUser } from 'containers/User/globalAdmin';
import { callCoreApi } from 'utils/request';
import type { AvailableModule, EligibleModule, TrainingAction, TrainingStep } from './types';
import type { TrainingContextId } from './contexts';
import { bindTourStepBeforeShow, createTourStepAttachHost, runActionChain } from './tourRunner';

/** Action-capture mode for the builder — which step's `before` chain we're appending to */
export interface ActionCapture {
  stepIndex: number;
  mode: 'click' | 'rightClick';
}

export interface RunTourOptions {
  steps: TrainingStep[];
  /** When set, completion + cancellation record progress for this module/version. */
  recordFor?: { moduleId: number; version: number };
}

// ---------------------------------------------------------------------------
// Builder state types
// ---------------------------------------------------------------------------

export interface BuilderModuleSummary {
  moduleId: number;
  name: string;
  description: string | null;
  status: 'draft' | 'published' | 'archived';
  currentVersion: number;
  stepCount: number;
  autoStart: boolean;
}

export interface BuilderState {
  /** Whether builder mode is active (drawer open on some scope) */
  active: boolean;
  /** Which context scope the builder is editing */
  context: TrainingContextId | null;
  /** Whether the drawer is showing the module list or the step editor */
  viewMode: 'list' | 'edit';
  /** All authorable modules for the active context (visible in list mode) */
  moduleList: BuilderModuleSummary[];
  /** Module being edited (null = creating new) */
  moduleId: number | null;
  /** Current draft steps in the builder */
  steps: TrainingStep[];
  /** Module metadata */
  name: string;
  description: string;
  /** Whether there are unsaved changes */
  dirty: boolean;
  /** Module status from DB */
  moduleStatus: 'draft' | 'published' | 'archived' | null;
  /** Current published version number */
  currentVersion: number;
  /** Whether the active version is set to autofire on mount */
  autoStart: boolean;
  /** Preview mode — tour playing without recording progress */
  previewing: boolean;
}

const INITIAL_BUILDER_STATE: BuilderState = {
  active: false,
  context: null,
  viewMode: 'list',
  moduleList: [],
  moduleId: null,
  steps: [],
  name: '',
  description: '',
  dirty: false,
  moduleStatus: null,
  currentVersion: 0,
  autoStart: false,
  previewing: false,
};

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

interface TrainingContextValue {
  /** Fetch eligible (autofire) modules for a context scope */
  fetchEligible: (context: TrainingContextId) => Promise<EligibleModule[]>;
  /** Fetch all available modules for a context scope (drawer view, includes completed) */
  fetchAvailable: (context: TrainingContextId) => Promise<AvailableModule[]>;
  /** Imperatively launch a Shepherd tour. Replay (no recordFor) skips progress writes. */
  runTour: (opts: RunTourOptions) => Promise<void>;
  /** Mark a module as completed (won't show again) */
  markComplete: (moduleId: number, version: number) => Promise<void>;
  /** Mark a module as skipped/cancelled */
  markSkipped: (moduleId: number, version: number) => Promise<void>;
  /** Drop the eligibility/availability caches — call after any module write. */
  invalidateCaches: () => void;
  /** Whether training is available (has auth) */
  enabled: boolean;
  /** Whether the current user can author training (global admin) */
  canAuthor: boolean;
  /** Auth token for API calls */
  authToken: string;
  /** Help drawer — which scope's drawer is open (null = closed) */
  helpDrawerContext: TrainingContextId | null;
  openHelp: (context: TrainingContextId) => void;
  closeHelp: () => void;
  /** Active action-capture session for the builder, or null */
  actionCapture: ActionCapture | null;
  startActionCapture: (stepIndex: number, mode: 'click' | 'rightClick') => void;
  cancelActionCapture: () => void;
  captureActionTarget: (targetId: string) => void;
  /** Append an action that doesn't need capture (hover, wait) to a step's before chain */
  addBeforeAction: (stepIndex: number, action: TrainingAction) => void;
  /** Remove an action from a step's before chain */
  removeBeforeAction: (stepIndex: number, actionIndex: number) => void;
  /** Builder state and actions */
  builder: BuilderState;
  /** Open the builder for a context — lands on the module list */
  openBuilder: (context: TrainingContextId) => Promise<void>;
  closeBuilder: () => void;
  /** Refresh the list of modules for the active builder context */
  refreshBuilderList: () => Promise<void>;
  /** Pick an existing module from the list and enter edit mode */
  selectBuilderModule: (moduleId: number) => Promise<void>;
  /** Enter edit mode for a brand-new (yet-to-be-saved) module */
  startNewBuilderModule: () => void;
  /** Return from the editor back to the module list */
  backToBuilderList: () => Promise<void>;
  setBuilderSteps: (steps: TrainingStep[]) => void;
  setBuilderMeta: (name: string, description: string) => void;
  setBuilderDirty: (dirty: boolean) => void;
  setBuilderModuleId: (id: number) => void;
  setBuilderModuleStatus: (status: 'draft' | 'published' | 'archived' | null) => void;
  setBuilderCurrentVersion: (version: number) => void;
  setBuilderAutoStart: (autoStart: boolean) => void;
  startPreview: () => void;
  stopPreview: () => void;
}

const TrainingCtx = createContext<TrainingContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTrainingContext(): TrainingContextValue {
  const ctx = useContext(TrainingCtx);
  if (!ctx) {
    throw new Error('useTrainingContext must be used within <TrainingProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function TrainingProvider({ children }: { children: React.ReactNode }) {
  const authToken = useAuthtoken() || '';
  const user = useUser();
  const canAuthor = isGlobalAdminUser(user);

  // Eligibility cache (simple in-memory, keyed by context)
  const eligibilityCache = useRef<Map<string, { data: EligibleModule[]; fetchedAt: number }>>(new Map());
  const availabilityCache = useRef<Map<string, { data: AvailableModule[]; fetchedAt: number }>>(new Map());
  const CACHE_TTL_MS = 60_000;

  // Help drawer state
  const [helpDrawerContext, setHelpDrawerContext] = useState<TrainingContextId | null>(null);

  // Builder state
  const [builder, setBuilder] = useState<BuilderState>(INITIAL_BUILDER_STATE);
  // Mirror of builder for use inside async callbacks that can't depend on
  // closure-stale builder values (e.g. startActionCapture awaits a chain replay).
  const builderRef = useRef(builder);
  useEffect(() => { builderRef.current = builder; }, [builder]);

  // Action capture state — when set, the next click/right-click on a tour-id'd
  // element gets recorded as a `before` action on the named step instead of
  // adding a new step.
  const [actionCapture, setActionCapture] = useState<ActionCapture | null>(null);

  // In-flight tour reference — only one tour at a time anywhere in the app.
  const activeTourRef = useRef<any>(null);

  const fetchEligible = useCallback(async (context: TrainingContextId): Promise<EligibleModule[]> => {
    if (!authToken) return [];

    const cached = eligibilityCache.current.get(context);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      return cached.data;
    }

    try {
      const resp = await callCoreApi<EligibleModule[]>(
        'training/getEligible',
        { app: 'cloud', context },
        authToken,
      );
      const data = resp.success && resp.data ? resp.data : [];
      eligibilityCache.current.set(context, { data, fetchedAt: Date.now() });
      return data;
    } catch {
      return [];
    }
  }, [authToken]);

  const fetchAvailable = useCallback(async (context: TrainingContextId): Promise<AvailableModule[]> => {
    if (!authToken) return [];

    const cached = availabilityCache.current.get(context);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      return cached.data;
    }

    try {
      const resp = await callCoreApi<AvailableModule[]>(
        'training/listForContext',
        { app: 'cloud', context },
        authToken,
      );
      const data = resp.success && resp.data ? resp.data : [];
      availabilityCache.current.set(context, { data, fetchedAt: Date.now() });
      return data;
    } catch {
      return [];
    }
  }, [authToken]);

  const markComplete = useCallback(async (moduleId: number, version: number) => {
    if (!authToken) return;
    try {
      await callCoreApi('training/recordProgress', {
        moduleId, version, action: 'completed',
      }, authToken);
      // Invalidate caches so completed module won't re-show / shows new completion state
      eligibilityCache.current.clear();
      availabilityCache.current.clear();
    } catch (err) {
      console.error('[Training] Failed to record completion:', err);
    }
  }, [authToken]);

  const invalidateCaches = useCallback(() => {
    eligibilityCache.current.clear();
    availabilityCache.current.clear();
  }, []);

  const markSkipped = useCallback(async (moduleId: number, version: number) => {
    if (!authToken) return;
    try {
      await callCoreApi('training/recordProgress', {
        moduleId, version, action: 'cancelled',
      }, authToken);
      eligibilityCache.current.clear();
      availabilityCache.current.clear();
    } catch (err) {
      console.error('[Training] Failed to record skip:', err);
    }
  }, [authToken]);

  /**
   * Imperatively launch a Shepherd tour. Used by autofire (TrainingScope),
   * replay (HelpDrawer), and preview (BuilderDrawer — preview path still
   * lives in TrainingScope for now to keep dirty-step buffer access local).
   *
   * If `recordFor` is omitted, completion/cancellation are silent — this is
   * how replay works without polluting progress.
   */
  const runTour = useCallback(async (opts: RunTourOptions) => {
    if (opts.steps.length === 0) return;

    // Cancel any in-flight tour before starting a new one.
    if (activeTourRef.current) {
      activeTourRef.current.cancel();
      activeTourRef.current = null;
    }

    const ShepherdModule = await import('shepherd.js');
    await import('shepherd.js/dist/css/shepherd.css');
    const Shepherd = ShepherdModule.default;

    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: { behavior: 'smooth', block: 'center' },
      },
    });

    opts.steps.forEach((step, index) => {
      const attachHost = createTourStepAttachHost(step.target, step.placement);
      tour.addStep({
        id: step.id,
        title: step.title || '(no title)',
        text: step.body || '(no body)',
        attachTo: attachHost.attachTo,
        buttons: [
          ...(index > 0 ? [{ text: 'Back', action: tour.back, secondary: true }] : []),
          {
            text: index === opts.steps.length - 1 ? 'Done' : 'Next',
            action: index === opts.steps.length - 1 ? tour.complete : tour.next,
          },
        ],
        beforeShowPromise: bindTourStepBeforeShow({
          target: step.target,
          before: step.before,
          fallbackTargets: step.fallbackTargets,
          placement: step.placement,
        }, attachHost),
      });
    });

    tour.on('complete', () => {
      if (opts.recordFor) {
        markComplete(opts.recordFor.moduleId, opts.recordFor.version);
      }
      activeTourRef.current = null;
    });
    tour.on('cancel', () => {
      if (opts.recordFor) {
        markSkipped(opts.recordFor.moduleId, opts.recordFor.version);
      }
      activeTourRef.current = null;
    });

    activeTourRef.current = tour;
    tour.start();
  }, [markComplete, markSkipped]);

  // -------------------------------------------------------------------------
  // Help drawer actions
  // -------------------------------------------------------------------------

  const openHelp = useCallback((context: TrainingContextId) => {
    setHelpDrawerContext(context);
  }, []);

  const closeHelp = useCallback(() => {
    setHelpDrawerContext(null);
  }, []);

  // -------------------------------------------------------------------------
  // Builder actions
  // -------------------------------------------------------------------------

  /**
   * Fetch the list of modules for a context (any status) and return a tidy
   * summary for the builder's list view. Filtering happens client-side
   * because getModules already returns the merged module + version rows.
   */
  const fetchBuilderList = useCallback(async (context: TrainingContextId): Promise<BuilderModuleSummary[]> => {
    if (!authToken) return [];
    try {
      const resp = await callCoreApi<any[]>('training/getModules', {}, authToken);
      const all = resp.success && Array.isArray(resp.data) ? resp.data : [];
      const summaries: BuilderModuleSummary[] = [];
      for (const mod of all) {
        // getModules pipeline can yield null entries for modules with no version row.
        if (!mod) continue;
        const appliesTo = typeof mod.applies_to_json === 'string'
          ? JSON.parse(mod.applies_to_json)
          : mod.applies_to_json;
        if (!appliesTo || appliesTo.app !== 'cloud' || appliesTo.context !== context) continue;
        const steps = typeof mod.steps_json === 'string'
          ? JSON.parse(mod.steps_json)
          : mod.steps_json;
        const rawAutoStart = mod.auto_start ?? mod.autoStart;
        const autoStart = rawAutoStart === undefined || rawAutoStart === null
          ? true
          : !!rawAutoStart;
        summaries.push({
          // Pipeline merges the version row over the module row, so `id` is the
          // version row's PK; the real module id sits on `module_id`. Prefer
          // module_id, fall back to id only for module rows that never got
          // joined with a version.
          moduleId: mod.module_id ?? mod.id,
          name: mod.name || '',
          description: mod.description ?? null,
          status: mod.status,
          currentVersion: mod.current_version ?? 0,
          stepCount: Array.isArray(steps) ? steps.length : 0,
          autoStart,
        });
      }
      return summaries;
    } catch (err) {
      console.error('[Training] Failed to load builder list:', err);
      return [];
    }
  }, [authToken]);

  const openBuilder = useCallback(async (context: TrainingContextId) => {
    if (!authToken) return;
    setBuilder({ ...INITIAL_BUILDER_STATE, active: true, context, viewMode: 'list' });
    const moduleList = await fetchBuilderList(context);
    setBuilder((prev) => (prev.active && prev.context === context
      ? { ...prev, moduleList }
      : prev));
  }, [authToken, fetchBuilderList]);

  const refreshBuilderList = useCallback(async () => {
    if (!builder.active || !builder.context) return;
    const moduleList = await fetchBuilderList(builder.context);
    setBuilder((prev) => (prev.active && prev.context
      ? { ...prev, moduleList }
      : prev));
  }, [builder.active, builder.context, fetchBuilderList]);

  const selectBuilderModule = useCallback(async (moduleId: number) => {
    if (!authToken) return;
    try {
      const resp = await callCoreApi<any>('training/getModule', { moduleId }, authToken);
      if (!resp.success || !resp.data) return;
      const mod = resp.data;

      // If a draft exists for this module, load it instead of the published
      // version — the user is conceptually continuing an in-progress edit,
      // not starting a new one. moduleStatus stays 'draft' so the editor
      // shows Save / Publish buttons (rather than "Edit New Version").
      const editing = mod.draft || mod;
      const isDraft = !!mod.draft || mod.status === 'draft';

      const steps = typeof editing.steps_json === 'string'
        ? JSON.parse(editing.steps_json)
        : editing.steps_json;
      const rawAutoStart = editing.auto_start ?? editing.autoStart;
      const autoStart = rawAutoStart === undefined || rawAutoStart === null
        ? true
        : !!rawAutoStart;
      setBuilder((prev) => ({
        ...prev,
        viewMode: 'edit',
        // module_id is the real module PK; mod.id ends up as the version row's
        // PK after the trainingGetModules pipeline merges version over module.
        moduleId: mod.module_id ?? mod.id,
        steps: Array.isArray(steps) ? steps : [],
        name: editing.name || mod.name || '',
        description: editing.description || mod.description || '',
        dirty: false,
        // When editing a draft of a published module, surface 'draft' to the
        // UI so the right action buttons appear, even though the module's
        // overall status is 'published'.
        moduleStatus: isDraft ? 'draft' : mod.status,
        currentVersion: mod.current_version ?? 0,
        autoStart,
        previewing: false,
      }));
    } catch (err) {
      console.error('[Training] Failed to load module:', err);
    }
  }, [authToken]);

  const startNewBuilderModule = useCallback(() => {
    setBuilder((prev) => ({
      ...prev,
      viewMode: 'edit',
      moduleId: null,
      steps: [],
      name: '',
      description: '',
      dirty: false,
      moduleStatus: null,
      currentVersion: 0,
      autoStart: false,
      previewing: false,
    }));
  }, []);

  const backToBuilderList = useCallback(async () => {
    if (!builder.context) return;
    const moduleList = await fetchBuilderList(builder.context);
    setBuilder((prev) => ({
      ...prev,
      viewMode: 'list',
      moduleList,
      // Clear the editor buffer so re-entering doesn't show stale state.
      moduleId: null,
      steps: [],
      name: '',
      description: '',
      dirty: false,
      moduleStatus: null,
      currentVersion: 0,
      autoStart: false,
      previewing: false,
    }));
  }, [builder.context, fetchBuilderList]);

  const closeBuilder = useCallback(() => {
    setBuilder(INITIAL_BUILDER_STATE);
  }, []);

  const setBuilderSteps = useCallback((steps: TrainingStep[]) => {
    setBuilder((prev) => ({ ...prev, steps, dirty: true }));
  }, []);

  const setBuilderMeta = useCallback((name: string, description: string) => {
    setBuilder((prev) => ({ ...prev, name, description, dirty: true }));
  }, []);

  const setBuilderDirty = useCallback((dirty: boolean) => {
    setBuilder((prev) => ({ ...prev, dirty }));
  }, []);

  const setBuilderModuleId = useCallback((id: number) => {
    setBuilder((prev) => ({ ...prev, moduleId: id }));
  }, []);

  const setBuilderModuleStatus = useCallback((status: 'draft' | 'published' | 'archived' | null) => {
    setBuilder((prev) => ({ ...prev, moduleStatus: status }));
  }, []);

  const setBuilderCurrentVersion = useCallback((version: number) => {
    setBuilder((prev) => ({ ...prev, currentVersion: version }));
  }, []);

  const setBuilderAutoStart = useCallback((autoStart: boolean) => {
    setBuilder((prev) => ({ ...prev, autoStart }));
  }, []);

  // -------------------------------------------------------------------------
  // Before-chain actions (capture + manual add/remove)
  // -------------------------------------------------------------------------

  const startActionCapture = useCallback(async (stepIndex: number, mode: 'click' | 'rightClick') => {
    // Replay the step's existing before-chain first, so transient UI it has
    // already authored (an opened context menu, an expanded panel) is on
    // screen and capturable when the overlay activates. Without this, the
    // user would have to manually re-trigger that state and race against
    // click-outside dismissals when they hit "+ Click".
    const step = builderRef.current.steps[stepIndex];
    if (step?.before && step.before.length > 0) {
      await runActionChain(step.before);
    }
    setActionCapture({ stepIndex, mode });
  }, []);

  const cancelActionCapture = useCallback(() => {
    setActionCapture(null);
  }, []);

  const appendBeforeAction = (steps: TrainingStep[], stepIndex: number, action: TrainingAction): TrainingStep[] => {
    const step = steps[stepIndex];
    if (!step) return steps;
    const before = step.before ? [...step.before, action] : [action];
    const next = [...steps];
    next[stepIndex] = { ...step, before };
    return next;
  };

  const captureActionTarget = useCallback((targetId: string) => {
    setActionCapture((capture) => {
      if (!capture) return null;
      setBuilder((prev) => ({
        ...prev,
        steps: appendBeforeAction(prev.steps, capture.stepIndex, { type: capture.mode, target: targetId }),
        dirty: true,
      }));
      return null;
    });
  }, []);

  const addBeforeAction = useCallback((stepIndex: number, action: TrainingAction) => {
    setBuilder((prev) => ({
      ...prev,
      steps: appendBeforeAction(prev.steps, stepIndex, action),
      dirty: true,
    }));
  }, []);

  const removeBeforeAction = useCallback((stepIndex: number, actionIndex: number) => {
    setBuilder((prev) => {
      const step = prev.steps[stepIndex];
      if (!step || !step.before) return prev;
      const before = step.before.filter((_, i) => i !== actionIndex);
      const next = [...prev.steps];
      next[stepIndex] = { ...step, before: before.length > 0 ? before : undefined };
      return { ...prev, steps: next, dirty: true };
    });
  }, []);

  const startPreview = useCallback(() => {
    setBuilder((prev) => ({ ...prev, previewing: true }));
  }, []);

  const stopPreview = useCallback(() => {
    setBuilder((prev) => ({ ...prev, previewing: false }));
  }, []);

  const value: TrainingContextValue = {
    fetchEligible,
    fetchAvailable,
    runTour,
    markComplete,
    markSkipped,
    invalidateCaches,
    enabled: !!authToken,
    canAuthor,
    authToken,
    helpDrawerContext,
    openHelp,
    closeHelp,
    builder,
    openBuilder,
    closeBuilder,
    refreshBuilderList,
    selectBuilderModule,
    startNewBuilderModule,
    backToBuilderList,
    setBuilderSteps,
    setBuilderMeta,
    setBuilderDirty,
    setBuilderModuleId,
    setBuilderModuleStatus,
    setBuilderCurrentVersion,
    setBuilderAutoStart,
    startPreview,
    stopPreview,
    actionCapture,
    startActionCapture,
    cancelActionCapture,
    captureActionTarget,
    addBeforeAction,
    removeBeforeAction,
  };

  return (
    <TrainingCtx.Provider value={value}>
      {children}
    </TrainingCtx.Provider>
  );
}
